import type { ReactElement } from 'react';
import { useEffect, useState, Children, cloneElement } from 'react';
import { detectLocation } from './detectLocation.js';
import { type GeoLocationPlace } from './location/types';
import usePrevious from '../utils/usePrevious';

interface WithLocationProps {
    location?: GeoLocationPlace,
    setLocation: (a: GeoLocationPlace) => void,

    children: ReactElement,
}

/**
 * A component that looks up the user's location and passes it as a prop
 * to its child. Also forwards any provided props to the child.
 *
 * It passes location = null to the child until the lookup finishes.
 */
export const WithLocation = ({ location: locationProp, setLocation: setLocationProp, children }: WithLocationProps) => {
    const [location, setLocation] = useState(locationProp);
    // Locations flow in both directions (like, location could be either a prop to the parent or a state-field updated
    // by children), which is kinda Wrong. Because of that, we need access to its previous value.
    const previousLocationProp = usePrevious(locationProp);

    // on mount
    useEffect(() => {
        // on desktop, maxMind does geocoding on page load
        // on mobile, navigator.geolocation is used instead, usually after a user action since the browser alert is noisy
        if (!location) {
            detectLocation(updateLocation);
            // googlePlacesLoaded was loaded with the page, so we need to trigger this event again
            $(document).trigger('googlePlacesLoaded');
        }
    }, []);

    // on location-prop-change
    useEffect(() => {
        if (locationProp && locationProp !== location && locationProp !== previousLocationProp) {
            setLocation(locationProp);
        }
    }, [locationProp, location, previousLocationProp, setLocation]); // locationProp changes should actually trigger this; the rest is linter-noise

    const updateLocation = (newLocation: GeoLocationPlace) => {
        setLocation(newLocation);
        setLocationProp(newLocation);
    };

    const renderChildren = () => {
        return Children.map(children, function (child) {
            return cloneElement(child, { location });
        });
    };

    return (
        <div>{renderChildren()}</div>
    );
};
